/**
 * The direct link to DockAutomation Tablet Browser — the Android app that
 * shows this site on the forklift tablets and holds the barcode gun.
 *
 * The app and the page are in one process, so nothing is broadcast between
 * them. Two things cross:
 *
 * - **In**, as a `dockautomation:device` DOM event on `window`, whose detail
 *   is `{ type: 'scan', data, symbology, source }`,
 *   `{ type: 'gun-status', connected, detail }`, `{ type: 'wifi', ... }` or
 *   `{ type: 'key', button: 'P1' | 'P2' | 'P3' }` (a side button, app 2.16).
 * - **Out**, through `window.DockAutomationDevice.postMessage(json)`, an
 *   object the app's WebView injects for this site's origin only. Each
 *   message is one flat command in the gun's vocabulary — see `gunFeedback.ts`
 *   for the design and the app's `ScannerContract` for the names.
 *
 * The object's presence is how the page knows where it is running. There is
 * no flag to set on a tablet and no script to load: in any other browser the
 * object does not exist and every call here is a no-op.
 *
 * Nothing in this file imports Vue or Nuxt, so it is plain logic under test.
 */

import { tidyScan } from '~/utils/scanTypes'
import type { BridgeCommand } from './gunFeedback'

export const DEVICE_EVENT = 'dockautomation:device'

type DeviceObject = { postMessage: (message: string) => void }

/** The app's handle, or null in any browser that is not Tablet Browser. */
export function deviceBridge(): DeviceObject | null {
  const device = (globalThis as { DockAutomationDevice?: DeviceObject }).DockAutomationDevice
  return device && typeof device.postMessage === 'function' ? device : null
}

/** Hand the app one command. False when there is no app to hand it to. */
export function postToDevice(command: BridgeCommand): boolean {
  const device = deviceBridge()
  if (!device) return false
  try {
    device.postMessage(JSON.stringify(command))
    return true
  } catch {
    // Feedback is a courtesy on top of a decision already made and recorded;
    // a refused message must never surface as an error on a workflow screen.
    return false
  }
}

/**
 * Opens Tablet Browser's own diagnostics screen (task 2018), still behind the diagnostics code
 * when one is set. Support reaches it with one click over SureMDM's remote screen, which cannot do
 * the three-finger hold. False when there is no app to ask.
 */
export function openDiagnostics(): boolean {
  return postToDevice({ command: 'open-diagnostics' })
}

/** Which scanner read it: the gun in the driver's hand, or the tablet's own. */
export type ScanSource = 'gun' | 'tablet'

export type DeviceEvent =
  | { kind: 'scan', code: string, source: ScanSource }
  | { kind: 'gun-status', connected: boolean, detail: string }
  | { kind: 'wifi', connected: boolean, ssid: string, rssi: number | null, internet: boolean }
  | { kind: 'key', button: SideKey }
  | { kind: 'capabilities', tabletId: string | null, tabletIdSent: boolean, keepScreenOn: ConsoleKeepScreenOn | null, appVersion: string, versionCode: number | null, cameraModes: string[], commands: string[], printCommands: string[], printRawPorts: number[], tabletToken: string | null }
  | { kind: 'camera-scan', requestId: string, status: CameraScanStatus, value: string, checkDigitValid: boolean | null, errorMessage: string }
  | { kind: 'print', requestId: string, status: PrintStatus, errorCode: string, errorMessage: string, elapsedMs: number }

/** How a print job ended (Tablet Browser 2.22, utils/scanSource/printing.ts). */
export type PrintStatus = 'ok' | 'cancelled' | 'error'
const PRINT_STATUSES: readonly string[] = ['ok', 'cancelled', 'error']

/** The tablet's own Keep the Screen On (BOB's tablet settings from 2.24), as Tablet Browser 2.21 on reports it in `capabilities`. */
export type ConsoleKeepScreenOn = 'never' | 'charging' | 'always'
const CONSOLE_KEEP_SCREEN_ON: readonly string[] = ['never', 'charging', 'always']

/** How a camera scan ended (Tablet Browser 2.15): a value, the driver's Cancel, or an error. */
export type CameraScanStatus = 'ok' | 'cancelled' | 'error'
const CAMERA_STATUSES: readonly string[] = ['ok', 'cancelled', 'error']

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string').map(item => item.trim()).filter(Boolean) : []
}

/** The tablet's programmable side buttons (Tablet Browser 2.16, see utils/sideKeys.ts). */
export type SideKey = 'P1' | 'P2' | 'P3'
const SIDE_KEYS: readonly string[] = ['P1', 'P2', 'P3']

/**
 * One event from the app, or null for anything that is not one.
 *
 * Only the TRAILING terminator is trimmed from a scan, as everywhere else: a
 * leading space is a real door prefix (see scanTypes), and prefix and grammar
 * decisions happen downstream, not here.
 */
export function readDeviceEvent(detail: unknown): DeviceEvent | null {
  if (!detail || typeof detail !== 'object') return null
  const fields = detail as Record<string, unknown>

  if (fields.type === 'gun-status' && typeof fields.connected === 'boolean') {
    return {
      kind: 'gun-status',
      connected: fields.connected,
      detail: typeof fields.detail === 'string' ? fields.detail : '',
    }
  }

  if (fields.type === 'wifi' && typeof fields.connected === 'boolean') {
    return {
      kind: 'wifi',
      connected: fields.connected,
      ssid: typeof fields.ssid === 'string' ? fields.ssid : '',
      rssi: typeof fields.rssi === 'number' && Number.isFinite(fields.rssi) ? fields.rssi : null,
      internet: fields.internet === true,
    }
  }

  if (fields.type === 'key' && typeof fields.button === 'string' && SIDE_KEYS.includes(fields.button)) {
    return { kind: 'key', button: fields.button as SideKey }
  }

  // The app's answer to `capabilities` (Tablet Browser 2.15 on; docs/tablets.md). From 2.21 it
  // carries `tabletId` (null when the console set none: `tabletIdSent` tells that from an older
  // app, which leaves the field out) and `keepScreenOn`, the console's own setting, whose
  // presence also says the app takes `keep-screen-on` (utils/scanSource/tabletInfo.ts).
  // `cameraModes` is what its camera scanner reads; `commands`, if an app ever lists them,
  // outranks everything else about which commands it knows.
  if (fields.type === 'capabilities') {
    const id = typeof fields.tabletId === 'string' ? fields.tabletId.trim() : ''
    const keep = typeof fields.keepScreenOn === 'string' ? fields.keepScreenOn.trim().toLowerCase() : ''
    return {
      kind: 'capabilities',
      tabletId: id === '' ? null : id,
      tabletIdSent: 'tabletId' in fields,
      keepScreenOn: CONSOLE_KEEP_SCREEN_ON.includes(keep) ? keep as ConsoleKeepScreenOn : null,
      appVersion: typeof fields.appVersion === 'string' ? fields.appVersion.trim() : '',
      versionCode: typeof fields.versionCode === 'number' && Number.isFinite(fields.versionCode) ? fields.versionCode : null,
      cameraModes: fields.camera === false ? [] : stringList(fields.cameraModes),
      commands: stringList(fields.commands),
      // What the app prints, from 2.22: an older app sends neither, and is never sent a print.
      printCommands: stringList(fields.printCommands),
      printRawPorts: Array.isArray(fields.printRawPorts)
        ? fields.printRawPorts.map(Number).filter(port => Number.isInteger(port) && port > 0)
        : [],
      // From 2.24: the approved tablet's own token for its log-in sessions (utils/tabletSession.ts);
      // null before a manager approves it in BOB, and absent from an older app.
      tabletToken: typeof fields.tabletToken === 'string' && fields.tabletToken.trim() ? fields.tabletToken.trim() : null,
    }
  }

  // The one answer to a `print-raw` or `print-page` request (2.22). Read by the screen that asked
  // (utils/scanSource/printing.ts). Any status but ok or cancelled is an error.
  if (fields.type === 'print' && typeof fields.requestId === 'string' && fields.requestId !== '') {
    return {
      kind: 'print',
      requestId: fields.requestId,
      status: (typeof fields.status === 'string' && PRINT_STATUSES.includes(fields.status) ? fields.status : 'error') as PrintStatus,
      errorCode: typeof fields.errorCode === 'string' ? fields.errorCode : '',
      errorMessage: typeof fields.errorMessage === 'string' ? fields.errorMessage : '',
      elapsedMs: typeof fields.elapsedMs === 'number' && Number.isFinite(fields.elapsedMs) ? fields.elapsedMs : 0,
    }
  }

  // The one answer to a `camera-scan` request (2.15). Read by the page that asked, never put on
  // the scan bus (utils/scanSource/cameraScan.ts). An answer with no requestId has no asker.
  if (fields.type === 'camera-scan' && typeof fields.requestId === 'string' && fields.requestId !== ''
    && typeof fields.status === 'string' && CAMERA_STATUSES.includes(fields.status)) {
    return {
      kind: 'camera-scan',
      requestId: fields.requestId,
      status: fields.status as CameraScanStatus,
      value: typeof fields.value === 'string' ? tidyScan(fields.value) : '',
      checkDigitValid: typeof fields.checkDigitValid === 'boolean' ? fields.checkDigitValid : null,
      errorMessage: typeof fields.errorMessage === 'string' ? fields.errorMessage : '',
    }
  }

  if (fields.type === 'scan' && typeof fields.data === 'string') {
    const code = tidyScan(fields.data)
    if (code === '') return null
    return { kind: 'scan', code, source: fields.source === 'tablet' ? 'tablet' : 'gun' }
  }

  return null
}
